import type { KeyboardEvent, ReactNode } from "react";
import { AlignCenter, AlignLeft, RotateCcw } from "lucide-react";
import { GRADIENT_ANGLES, HEADING_SCALES, SECTION_PATTERNS, type SectionBackground, type SectionNode, type SectionStyle, type WebsiteDefinition } from "@baze/schema";
import { readableOn } from "@baze/engine";
import { Segmented } from "@/components/ui/segmented";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { themeFor } from "@/lib/catalog";
import { useCatalog } from "@/lib/queries";
import { updateSectionStyle } from "../model/ops";
import { ColorField } from "./ColorField";
import { EmphasisPicker } from "./EmphasisPicker";
import { MotionPicker } from "./MotionPicker";
import { SectionThemeGroup, TextStyleList } from "./SectionTheme";
import { PATTERN_PREVIEW } from "./presets";
import { siteSwatches } from "./StylePanel";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type BgKind = "default" | SectionBackground["kind"];
type Angle = (typeof GRADIENT_ANGLES)[number];

/** Per-section styling inside the "Tampilan" block: background, pattern, text colour, alignment, heading size. */
export function SectionStyleCard({ def, section, apply }: { def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  const catalog = useCatalog();
  const theme = catalog.data ? themeFor(catalog.data, def.theme) : null;
  if (!theme) return null;
  const c = theme.tokens.colors;
  const style: SectionStyle = section.style ?? {};
  const bg = style.bg;
  const set = (patch: Partial<SectionStyle>, key?: string) => apply(updateSectionStyle(def, section.id, patch), key && `style:${section.id}:${key}`);
  const swatches = siteSwatches(theme);
  const navbar = section.type === "navbar";
  const title = (section.content as { title?: unknown }).title;

  const switchKind = (kind: BgKind) => {
    const seed = bg?.kind === "solid" ? bg.color : bg?.kind === "gradient" ? bg.from : bg?.kind === "mesh" ? bg.a : c.surface;
    if (kind === "default") set({ bg: undefined });
    else if (kind === "solid") set({ bg: { kind, color: seed } });
    else if (kind === "gradient") set({ bg: { kind, from: seed, to: c.primary, angle: 135 } });
    else set({ bg: { kind, a: seed, b: c.accent, c: c.primary } });
  };
  // What the canvas will show as the section's base colour (for the "auto" text preview).
  const base = bg?.kind === "solid" ? bg.color : bg?.kind === "gradient" ? bg.from : bg?.kind === "mesh" ? bg.a : c.background;

  return (
    <div className="space-y-5">
      {typeof title === "string" && title.trim() && section.type !== "footer" ? (
        <Block label={t.editor.style.emphasis.title}>
          <EmphasisPicker title={title} emphasis={style.emphasis} theme={theme} swatches={swatches} onChange={(emphasis, key) => set({ emphasis }, key)} />
        </Block>
      ) : null}
      <SectionThemeGroup def={def} section={section} theme={theme} apply={apply} />
      <TextStyleList def={def} section={section} apply={apply} />
      <Block label={t.editor.style.section.background}>
        <Segmented
          size="sm"
          full
          label={t.editor.style.section.background}
          value={bg?.kind ?? "default"}
          onChange={switchKind}
          options={(["default", "solid", "gradient", "mesh"] as const).map((k) => ({ value: k, label: t.editor.style.section.bgKinds[k] }))}
        />
        {bg ? <BackgroundPreview bg={bg} /> : null}
        {bg?.kind === "solid" ? (
          <ColorField size="chip" className="w-full" label={t.editor.style.section.color} value={bg.color} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, color: hex } }, "bg")} />
        ) : null}
        {bg?.kind === "gradient" ? (
          <div className="flex items-center gap-3">
            <div className="grid min-w-0 flex-1 gap-2">
              <ColorField size="chip" label={t.editor.style.section.from} value={bg.from} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, from: hex } }, "bg")} />
              <ColorField size="chip" label={t.editor.style.section.to} value={bg.to} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, to: hex } }, "bg")} />
            </div>
            <AngleDial value={bg.angle as Angle} onChange={(angle) => set({ bg: { ...bg, angle } })} />
          </div>
        ) : null}
        {bg?.kind === "mesh" ? (
          <div className="grid gap-2">
            <ColorField size="chip" label={t.editor.style.section.meshBase} value={bg.a} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, a: hex } }, "bg")} />
            <div className="grid grid-cols-2 gap-2">
              <ColorField size="chip" label={t.editor.style.section.meshGlow1} value={bg.b} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, b: hex } }, "bg")} />
              <ColorField size="chip" label={t.editor.style.section.meshGlow2} value={bg.c} swatches={swatches} onChange={(hex) => set({ bg: { ...bg, c: hex } }, "bg")} />
            </div>
          </div>
        ) : null}
      </Block>

      {navbar ? null : (
        <Block label={t.editor.style.section.pattern}>
          <div className="grid grid-cols-5 gap-1.5">
            {SECTION_PATTERNS.map((p) => {
              const active = (style.pattern ?? "none") === p;
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={active}
                  onClick={() => set({ pattern: p === "none" ? undefined : p })}
                  className="group min-w-0 text-center outline-none"
                >
                  <span
                    className="block aspect-square rounded-lg ring-1 ring-line transition group-hover:ring-slate-300 group-focus-visible:ring-2 group-focus-visible:ring-primary group-aria-pressed:ring-2 group-aria-pressed:ring-primary group-aria-pressed:ring-offset-1"
                    style={{ backgroundColor: base, color: style.text && style.text !== "auto" ? style.text : readableOn(base), ...PATTERN_PREVIEW[p] }}
                  />
                  <span className={cn("mt-1 block truncate text-[11px] font-medium", active ? "text-slate-900" : "text-slate-500")}>{t.editor.style.section.patterns[p]}</span>
                </button>
              );
            })}
          </div>
        </Block>
      )}

      <Block label={t.editor.style.section.text}>
        <div className="flex items-center gap-2">
          <Segmented
            size="sm"
            label={t.editor.style.section.text}
            value={style.text === undefined ? "default" : style.text === "auto" ? "auto" : "custom"}
            onChange={(v) => set({ text: v === "default" ? undefined : v === "auto" ? "auto" : readableOn(base) })}
            options={(["default", "auto", "custom"] as const).map((k) => ({ value: k, label: t.editor.style.section.textModes[k] }))}
          />
          {style.text && style.text !== "auto" ? (
            <ColorField size="chip" className="min-w-0 flex-1" label={t.editor.style.section.textColor} value={style.text} swatches={swatches} onChange={(hex) => set({ text: hex }, "text")} />
          ) : null}
        </div>
        {style.text === "auto" ? <p className="text-[12px] text-slate-500">{t.editor.style.section.autoHint}</p> : null}
      </Block>

      {navbar ? null : (
        <div className="grid gap-4">
          <Block label={t.editor.style.section.align}>
            <Segmented
              size="sm"
              label={t.editor.style.section.align}
              value={style.align ?? "start"}
              onChange={(v) => set({ align: v === "start" ? undefined : v })}
              options={[
                { value: "start", label: <AlignLeft aria-hidden="true" />, ariaLabel: t.editor.style.section.alignStart },
                { value: "center", label: <AlignCenter aria-hidden="true" />, ariaLabel: t.editor.style.section.alignCenter },
              ]}
            />
          </Block>
          <Block label={t.editor.style.section.headingScale}>
            <Segmented
              size="sm"
              full
              label={t.editor.style.section.headingScale}
              value={style.headingScale ?? "md"}
              onChange={(v) => set({ headingScale: v === "md" ? undefined : v })}
              options={HEADING_SCALES.map((s) => ({ value: s, label: s.toUpperCase(), ariaLabel: t.editor.style.section.scales[s] }))}
            />
          </Block>
        </div>
      )}

      {navbar || section.type === "footer" ? null : (
        <Block label={t.editor.style.section.motion.title}>
          <MotionPicker
            sectionId={section.id}
            sectionType={section.type}
            motion={style.motion}
            designStyle={theme.tokens.motion}
            onChange={(motion) => set({ motion })}
          />
        </Block>
      )}

      {section.style ? (
        <button type="button" onClick={() => apply(updateSectionStyle(def, section.id, null))} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900">
          <RotateCcw className="size-3.5" aria-hidden="true" />
          {t.editor.style.section.reset}
        </button>
      ) : null}
    </div>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[12px] font-medium text-slate-500">{label}</p>
      {children}
    </div>
  );
}

/** The chosen background as a strip, painted the same way the engine paints it. */
function BackgroundPreview({ bg }: { bg: SectionBackground }) {
  const background =
    bg.kind === "solid"
      ? bg.color
      : bg.kind === "gradient"
        ? `linear-gradient(${bg.angle}deg, ${bg.from}, ${bg.to})`
        : `radial-gradient(60% 70% at 12% 18%, ${bg.b}d9, transparent 70%), radial-gradient(55% 65% at 88% 30%, ${bg.c}cc, transparent 70%), ${bg.a}`;
  return <div className="h-10 rounded-lg ring-1 ring-slate-900/10 ring-inset" style={{ background }} aria-hidden="true" />;
}

/** Eight compass stops around a ring; the needle shows the gradient's direction. Arrow keys rotate. */
function AngleDial({ value, onChange }: { value: Angle; onChange: (a: Angle) => void }) {
  const step = (dir: 1 | -1) => onChange(GRADIENT_ANGLES[(GRADIENT_ANGLES.indexOf(value) + dir + GRADIENT_ANGLES.length) % GRADIENT_ANGLES.length]!);
  const onKey = (e: KeyboardEvent) => {
    const dir = e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    step(dir);
  };
  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={t.editor.style.section.angle}
      aria-valuemin={0}
      aria-valuemax={315}
      aria-valuenow={value}
      aria-valuetext={`${value}°`}
      onKeyDown={onKey}
      className="relative size-[5.25rem] shrink-0 rounded-full bg-slate-50 ring-1 ring-line outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {GRADIENT_ANGLES.map((a) => {
        const rad = ((a - 90) * Math.PI) / 180;
        return (
          <button
            key={a}
            type="button"
            tabIndex={-1}
            aria-label={`${a}°`}
            onClick={() => onChange(a)}
            className={cn("absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full transition", a === value ? "scale-125 bg-primary" : "bg-slate-300 hover:bg-slate-400")}
            style={{ left: `${50 + Math.cos(rad) * 38}%`, top: `${50 + Math.sin(rad) * 38}%` }}
          />
        );
      })}
      <span className="pointer-events-none absolute inset-0 grid place-items-center transition-transform duration-200" style={{ transform: `rotate(${value}deg)` }} aria-hidden="true">
        <span className="relative h-7 w-[3px] -translate-y-2 rounded-full bg-slate-800 after:absolute after:-top-1 after:left-1/2 after:size-2 after:-translate-x-1/2 after:rotate-45 after:border-t-[3px] after:border-l-[3px] after:border-slate-800" />
      </span>
    </div>
  );
}
